import React from 'react';
import {
    SaveOutlined,
    CloseOutlined,
    DeleteOutlined,
    PlusOutlined,
    EditOutlined,
    DownloadOutlined,
    UploadOutlined,
    CheckOutlined,
    StopOutlined,
    ReloadOutlined,
    SearchOutlined,
    FilterOutlined,
    ExportOutlined,
    CopyOutlined,
    SendOutlined
} from '@ant-design/icons';

/**
 * Standard Button Preset Configurations
 *
 * These presets provide consistent button configurations across the application.
 * They are optional helpers - developers can still create custom configurations.
 *
 * Usage:
 * import { BUTTON_PRESETS } from '@/design-system/constants/buttonPresets';
 *
 * <Modal.Footer
 *   buttons={[
 *     { ...BUTTON_PRESETS.CANCEL, label: 'Cancel', onClick: handleCancel },
 *     { ...BUTTON_PRESETS.SAVE, label: 'Save Changes', onClick: handleSave }
 *   ]}
 * />
 */

export const BUTTON_PRESETS = {
    // Primary Actions
    SAVE: {
        variant: 'primary',
        icon: <SaveOutlined />,
        hoverColor: '#fad133' // Yellow on hover
    },

    ADD: {
        variant: 'primary',
        icon: <PlusOutlined />,
        hoverColor: '#fad133'
    },

    SUBMIT: {
        variant: 'primary',
        icon: <CheckOutlined />,
        hoverColor: '#fad133'
    },

    SEND: {
        variant: 'primary',
        icon: <SendOutlined />,
        hoverColor: '#fad133'
    },

    // Secondary Actions
    CANCEL: {
        variant: 'secondary',
        icon: <CloseOutlined />,
        hoverColor: '#FD3635' // Red on hover for cancel/close
    },

    CLOSE: {
        variant: 'secondary',
        icon: <CloseOutlined />,
        hoverColor: '#FD3635'
    },

    EDIT: {
        variant: 'secondary',
        icon: <EditOutlined />,
        hoverColor: '#fad133'
    },

    DOWNLOAD: {
        variant: 'secondary',
        icon: <DownloadOutlined />,
        hoverColor: '#4CAF50' // Green for download
    },

    UPLOAD: {
        variant: 'secondary',
        icon: <UploadOutlined />,
        hoverColor: '#4CAF50'
    },

    EXPORT: {
        variant: 'secondary',
        icon: <ExportOutlined />,
        hoverColor: '#4CAF50'
    },

    SEARCH: {
        variant: 'secondary',
        icon: <SearchOutlined />,
        hoverColor: '#2196F3' // Blue for search
    },

    FILTER: {
        variant: 'secondary',
        icon: <FilterOutlined />,
        hoverColor: '#2196F3'
    },

    COPY: {
        variant: 'secondary',
        icon: <CopyOutlined />,
        hoverColor: '#9C27B0' // Purple for copy
    },

    REFRESH: {
        variant: 'secondary',
        icon: <ReloadOutlined />,
        hoverColor: '#FF9800' // Orange for refresh
    },

    // Danger Actions
    DELETE: {
        variant: 'danger',
        icon: <DeleteOutlined />
        // Danger variant has its own hover color
    },

    STOP: {
        variant: 'danger',
        icon: <StopOutlined />
    }
};

/**
 * Common button combinations for modal footers
 *
 * Usage:
 * import { BUTTON_COMBINATIONS } from '@/design-system/constants/buttonPresets';
 *
 * <Modal.Footer
 *   buttons={BUTTON_COMBINATIONS.CANCEL_SAVE(handleCancel, handleSave)}
 * />
 */
export const BUTTON_COMBINATIONS = {
    // Cancel + Save pattern
    CANCEL_SAVE: (onCancel, onSave, saveLabel = 'Save', saveLoading = false) => [
        {
            ...BUTTON_PRESETS.CANCEL,
            key: 'cancel',
            label: 'Cancel',
            onClick: onCancel
        },
        {
            ...BUTTON_PRESETS.SAVE,
            key: 'save',
            label: saveLabel,
            onClick: onSave,
            loading: saveLoading
        }
    ],

    // Cancel + Delete pattern
    CANCEL_DELETE: (onCancel, onDelete, deleteLabel = 'Delete', deleteLoading = false) => [
        {
            ...BUTTON_PRESETS.CANCEL,
            key: 'cancel',
            label: 'Cancel',
            onClick: onCancel
        },
        {
            ...BUTTON_PRESETS.DELETE,
            key: 'delete',
            label: deleteLabel,
            onClick: onDelete,
            loading: deleteLoading
        }
    ],

    // Close only pattern
    CLOSE_ONLY: (onClose) => [
        {
            ...BUTTON_PRESETS.CLOSE,
            key: 'close',
            label: 'Close',
            onClick: onClose
        }
    ],

    // Cancel + Add pattern
    CANCEL_ADD: (onCancel, onAdd, addLabel = 'Add', addLoading = false) => [
        {
            ...BUTTON_PRESETS.CANCEL,
            key: 'cancel',
            label: 'Cancel',
            onClick: onCancel
        },
        {
            ...BUTTON_PRESETS.ADD,
            key: 'add',
            label: addLabel,
            onClick: onAdd,
            loading: addLoading
        }
    ]
};

// Export individual icons for custom use
export const BUTTON_ICONS = {
    SAVE: <SaveOutlined />,
    CLOSE: <CloseOutlined />,
    CANCEL: <CloseOutlined />,
    DELETE: <DeleteOutlined />,
    ADD: <PlusOutlined />,
    EDIT: <EditOutlined />,
    DOWNLOAD: <DownloadOutlined />,
    UPLOAD: <UploadOutlined />,
    CHECK: <CheckOutlined />,
    STOP: <StopOutlined />,
    REFRESH: <ReloadOutlined />,
    SEARCH: <SearchOutlined />,
    FILTER: <FilterOutlined />,
    EXPORT: <ExportOutlined />,
    COPY: <CopyOutlined />,
    SEND: <SendOutlined />
};

// Standard hover colors for consistency
export const HOVER_COLORS = {
    PRIMARY: '#fad133',      // Yellow - brand color
    CANCEL: '#FD3635',       // Red - for cancel/close
    SUCCESS: '#4CAF50',      // Green - for success actions
    INFO: '#2196F3',         // Blue - for info actions
    WARNING: '#FF9800',      // Orange - for warning actions
    SECONDARY: '#9C27B0'     // Purple - for secondary actions
};